import {
  ArrowLeftRight,
  ChevronsUpDown,
  Lock,
  LogOut,
  User,
} from "lucide-react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useUser } from "@/hooks/useUser.ts";
import { nameToInitials } from "@/lib/utils.ts";
import { Link } from "react-router";
import { logout } from "@/api/common/interceptors/authenticationInterceptor.ts";
import SwitchAccountDialogContent from "@/components/SwitchAccountDialogContent.tsx";
import { Dialog, DialogTrigger } from "@/components/ui/dialog.tsx";
import { useState } from "react";
import { useTranslation } from "@/i18n";

const NavUser = () => {
  const { isMobile } = useSidebar();
  const [switchClientDialogOpen, setSwitchClientDialogOpen] = useState(false);
  const user = useUser();
  const { t } = useTranslation();
  if (!user) return null;

  return (
    <Dialog
      open={switchClientDialogOpen}
      onOpenChange={setSwitchClientDialogOpen}
    >
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarFallback className="rounded-lg">
                    {nameToInitials(user.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{user.name}</span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
                <ChevronsUpDown className="ml-auto size-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              side={isMobile ? "bottom" : "right"}
              align="end"
              sideOffset={4}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarFallback className="rounded-lg">
                      {nameToInitials(user.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{user.name}</span>
                    <span className="truncate text-xs">{user.email}</span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DialogTrigger asChild>
                  <DropdownMenuItem>
                    <ArrowLeftRight />
                    {t("nav.switchClient")}
                  </DropdownMenuItem>
                </DialogTrigger>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <Link to="/profile">
                  <DropdownMenuItem>
                    <User />
                    {t("common.profile")}
                  </DropdownMenuItem>
                </Link>
                <Link to="/change-password">
                  <DropdownMenuItem>
                    <Lock />
                    {t("nav.changePassword")}
                  </DropdownMenuItem>
                </Link>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive hover:text-destructive focus:text-destructive"
                onClick={logout}
              >
                <LogOut className="text-destructive" />
                {t("common.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
      <SwitchAccountDialogContent
        onClose={() => setSwitchClientDialogOpen(false)}
      />
    </Dialog>
  );
};

export default NavUser;
